import * as THREE from 'three';
import { BoardScene } from './play/BoardScene';
import { PlaySession } from './play/PlaySession';
import { cellCenter, worldToScreen, type Viewport } from './play/layout';
import { HANDOVER_MS, PopupScene, type PopupLayer, type TapResult } from './scenes/PopupScene';
import { MAP_MAX_TILT, MapScene, mapRect, type HotspotState } from './scenes/MapScene';
import { MAX_TILT_DEG, type Gyro } from './core/gyro';
import { setHapticsEnabled, tapFeedback } from './core/haptics';
import { today } from './core/clock';
import { dailyPuzzle, type DailyPuzzle } from './core/daily';
import { dailyMods, gridKey, gridLabel, levelGrid, MAX_N, MIN_N, square, TAROT_GRIDS, themeLevelMods, type Grid, type LevelMods } from './core/levels';
import {
  addDays, allDialsSolved, cityLocks, completedCount, currentStreak, dialSave, dialSolved, inProgressGrid, reconcile, recordCompletion, recordDaily, totalStars,
  type CompletionResult, type DailyRecord, type InProgress, type PlayMode, type SaveStore, type ThemeGate, type ThemeSave,
} from './core/storage';
import { chapterOf, imageAspect, type Manifest, type Place, type ThemeEntry } from './content/manifest';
import { dialOf, puzzlesOf } from './content/puzzles';
import { DialView, type DialModel } from './ui/dialPage';
import { loadLayers, loadTexture } from './content/textures';
import { DayNight } from './fx/glow';
import type { Hud } from './ui/hud';
import type { DialMarker, MapUi, TeaserLabel } from './ui/mapUi';
import type { IntroKind, Onboarding } from './ui/onboarding';
import { canRotate, rowsOf, tileCount } from './puzzle';
import { PlaceCard, cancelConfirm, confirmDialog, type CardModel } from './ui/placeCard';
import type { AlbumCell, CalendarCell, Pages } from './ui/pages';

export type Page = 'home' | 'cities' | 'map' | 'card' | 'play' | 'popup' | 'viewPopup' | 'ending' | 'daily' | 'album' | 'settings' | 'dial';
type DomPage = 'home' | 'cities' | 'daily' | 'album' | 'settings' | 'dial';

const DRAG_THRESHOLD = 8;
const CN_NUM = ['一', '二', '三', '四', '五', '六', '七', '八', '九', '十'];
/** 揭纸播完后多久开始终章 */
const ENDING_DELAY_MS = 900;
/** 每日挑战页的打卡日历：最近 4 周 */
const CAL_WEEKS = 4;

type Gesture = { id: number; x: number; y: number; dragging: boolean };

type PlayRes = {
  mode: PlayMode;
  themeId: string;
  /** 每日挑战的日期 */
  date: string | null;
  place: Place;
  board: BoardScene;
  session: PlaySession;
  /** 每日挑战没有立体书（v2 spec §4） */
  popup: PopupScene | null;
  result: CompletionResult | null;
  daily: DailyRecord | null;
  /** 本局是否在走新手引导第 1 步 */
  onboardDrag: boolean;
};
type ViewRes = { themeId: string; place: Place; layers: PopupLayer[]; popup: PopupScene; from: 'map' | 'album' };

/** n = 列数，rows = 行数（缺省 = n，方形） */
export type PlaySpec = { mode: PlayMode; themeId: string; placeId: string; n: number; rows?: number; seed?: number | null; date?: string | null };

const specGrid = (s: PlaySpec): Grid => ({ cols: s.n, rows: s.rows ?? s.n });
const gridSpec = (g: Grid): { n: number; rows?: number } => (g.rows === g.cols ? { n: g.cols } : { n: g.cols, rows: g.rows });

export type AppDeps = {
  renderer: THREE.WebGLRenderer;
  themes: ThemeEntry[];
  /** 与 themes 一一对应（同顺序） */
  manifests: Manifest[];
  store: SaveStore;
  gyro: Gyro;
  hud: Hud;
  mapUi: MapUi;
  onboarding: Onboarding;
  pages: Pages;
  glowTexture: THREE.Texture;
  version: string;
  /** 开发 / 测试：固定洗牌种子（旅程模式） */
  seed: number | null;
  /** 每次换页（main.ts 用它维护浏览器历史里的返回守卫） */
  onPage?: (p: Page) => void;
};

/**
 * 页面状态机 + 输入路由 + 每帧推进（v1 spec §8 app.ts，v2 spec §4）：
 * 首页 → 城市选择 → 城市地图 → 地点卡 → 拼图 → 立体书 → 地图 → 终章；首页 → 每日挑战 / 图鉴 / 设置。
 * 返回（左上按钮、浏览器后退、Android 返回键）都走 back()：一律回到上一级。
 */
export class App {
  page: Page = 'home';
  /** 当前城市（地图 / 地点卡 / 旅程拼图） */
  themeId: string;
  map: MapScene | null = null;
  private mapTheme: string | null = null;
  /** 终章的立体地图（ending 页，以及终章之后的地图页） */
  town: PopupScene | null = null;
  private townLayers: PopupLayer[] | null = null;
  play: PlayRes | null = null;
  view: ViewRes | null = null;
  readonly card: PlaceCard;
  readonly dayNight: DayNight;
  /** 测试钩子注入的小时；null = 本地时间 */
  hourOverride: number | null = null;
  lastTap: TapResult | null = null;
  /** 地图上最近一次点按的判定（测试用） */
  lastMapTap: { id: string | null; state: HotspotState | null } | null = null;
  private albumTab: string;
  private vp: Viewport = { width: 1, height: 1 };
  private gesture: Gesture | null = null;
  private resultShown = false;
  private endingStoryShown = false;
  /** 回到地图时要播放的揭纸 / 解锁 */
  private reveal: { themeId: string; completed: string; unlocked: string | null } | null = null;
  /** 解开星盘后新开放的地点（回地图时播放解锁） */
  private unlockReveal: { themeId: string; ids: string[] } | null = null;
  /** 星盘页（月亮塔罗） */
  private dialView: DialView | null = null;
  dialChapter = -1;
  /** 刚开放的下一座城（回地图时提示一次） */
  private cityOpened: string | null = null;
  private endingIn = -1;
  /** 当前拼图已经计入 stats.totalPlayMs 的时长（session.playedMs 的快照） */
  private countedMs = 0;
  private busy = false;
  private seq = 0;
  private readonly byTheme = new Map<string, { entry: ThemeEntry; manifest: Manifest; places: Place[] }>();

  constructor(private readonly d: AppDeps) {
    d.themes.forEach((entry, i) => {
      const manifest = d.manifests[i];
      this.byTheme.set(entry.id, { entry, manifest, places: [...manifest.places].sort((a, b) => a.unlockOrder - b.unlockOrder) });
    });
    this.themeId = this.save.lastThemeId && this.byTheme.has(this.save.lastThemeId) ? this.save.lastThemeId : d.themes[0].id;
    this.albumTab = this.themeId;
    this.dayNight = new DayNight(this.hour());
    this.card = new PlaceCard({
      start: () => void this.startFromCard(true),
      resume: () => void this.startFromCard(false),
      view: () => void this.enterView(this.themeId, this.card.model!.placeId, 'map'),
      close: () => this.closeCard(),
    });
    d.hud.bindHome(() => void this.resultAction());
    d.hud.bindBack(() => void this.back());
    d.mapUi.onDial = (c) => this.openDial(c);
    d.gyro.onPermission = (asked, granted) => {
      this.save.gyro = { asked, granted };
      void this.d.store.flush();
    };
    this.applySettings();
  }

  get renderer(): THREE.WebGLRenderer {
    return this.d.renderer;
  }

  get gyro(): Gyro {
    return this.d.gyro;
  }

  get save() {
    return this.d.store.save;
  }

  get session(): PlaySession | null {
    return this.play?.session ?? null;
  }

  /** 当前城市的 manifest / 地点（按 unlockOrder） */
  get manifest(): Manifest {
    return this.byTheme.get(this.themeId)!.manifest;
  }

  get places(): Place[] {
    return this.byTheme.get(this.themeId)!.places;
  }

  get themeIds(): string[] {
    return this.d.themes.map((t) => t.id);
  }

  /** 月亮塔罗（alwaysOpen 的主题）：首页独立入口，不在旅程里 */
  get tarotId(): string | null {
    return this.d.themes.find((t) => this.byTheme.get(t.id)?.manifest.theme.alwaysOpen)?.id ?? null;
  }

  private isTarot(id = this.themeId): boolean {
    return !!this.byTheme.get(id)?.manifest.theme.alwaysOpen;
  }

  /** 解锁链：城市一座接一座开放；alwaysOpen 的主题（塔罗）开局就开放、不在链上 */
  private get gates(): ThemeGate[] {
    return this.d.themes.map((t) => ({ id: t.id, alwaysOpen: !!this.byTheme.get(t.id)?.manifest.theme.alwaysOpen }));
  }

  private themeSave(id = this.themeId): ThemeSave {
    return this.save.themes[id];
  }

  private placeOf(themeId: string, placeId: string): Place {
    const p = this.byTheme.get(themeId)?.places.find((q) => q.id === placeId);
    if (!p) throw new Error(`unknown place ${themeId}/${placeId}`);
    return p;
  }

  hour(): number {
    return this.hourOverride ?? new Date().getHours() + new Date().getMinutes() / 60;
  }

  private setPage(p: Page): void {
    if (p !== 'dial' && this.dialView) {
      this.dialView.dispose();
      this.dialView = null;
      this.dialChapter = -1;
    }
    this.page = p;
    document.querySelector<HTMLElement>('#app')!.dataset.page = p;
    this.gesture = null;
    if (!this.isDomPage(p)) this.d.pages.hide();
    this.d.onPage?.(p);
  }

  private isDomPage(p: Page): p is DomPage {
    return p === 'home' || p === 'cities' || p === 'daily' || p === 'album' || p === 'settings' || p === 'dial';
  }

  private stateOf(id: string): HotspotState {
    const s = this.themeSave().places[id];
    return s?.completed ? 'completed' : s?.unlocked ? 'unlocked' : 'locked';
  }

  get allCompleted(): boolean {
    return this.places.every((p) => this.themeSave().places[p.id]?.completed);
  }

  /** 终章可以开始了：全部通关，且（有星盘的主题）星盘全部解开 */
  get endingReady(): boolean {
    return this.allCompleted && allDialsSolved(this.save, this.themeId);
  }

  /** 终章之后，地图本身就是立体的 */
  get townIs3D(): boolean {
    return this.themeSave().endingSeen && this.endingReady;
  }

  /** 设置页的开关真正作用到震动和陀螺仪 */
  private applySettings(): void {
    setHapticsEnabled(this.save.settings.haptics);
    this.d.gyro.setEnabled(this.save.settings.gyro);
  }

  setViewport(vp: Viewport): void {
    this.vp = vp;
    this.map?.setViewport(vp);
    this.town?.setViewport(vp);
    this.view?.popup.setViewport(vp);
    if (this.play) {
      this.play.session.setViewport(vp);
      this.play.popup?.setViewport(vp);
    }
  }

  // ── DOM 页面：首页 / 城市选择 / 每日挑战 / 图鉴 / 设置 ──

  /** 离开 3D 页面去 DOM 页：释放场景，换标题 */
  private enterDom(p: DomPage, title: string): void {
    this.d.gyro.setActive(false);
    this.card.hide();
    this.d.hud.setPopup(false);
    this.d.hud.hideResult();
    this.d.mapUi.hideEnding();
    this.disposePlace();
    this.disposeMap();
    this.disposeTown();
    this.endingIn = -1;
    this.d.hud.setTitle(title);
    this.setPage(p);
  }

  showHome(): void {
    const ip = this.liveInProgress();
    let continueLabel: string;
    if (ip?.mode === 'daily') continueLabel = `每日挑战（${gridLabel(inProgressGrid(ip))}）`;
    else if (ip) continueLabel = `${this.placeOf(ip.themeId, ip.placeId).title}（${gridLabel(inProgressGrid(ip))}）`;
    else continueLabel = `回到${this.byTheme.get(this.continueTheme())!.entry.title}`;
    const t = today();
    const entry = this.byTheme.get(this.continueTheme())!.entry;
    const tid = this.tarotId;
    let tarot: Parameters<Pages['showHome']>[0]['tarot'] = null;
    if (tid) {
      const th = this.byTheme.get(tid)!;
      const ps = this.themeSave(tid).places;
      tarot = {
        title: th.entry.title,
        cover: th.places[0].thumb,
        lit: completedCount(this.save, tid),
        total: th.places.length,
        runes: th.places.filter((p) => p.rune && ps[p.id]?.runeFound).length,
        runeTotal: th.places.filter((p) => p.rune).length,
      };
    }
    this.enterDom('home', '纸上小镇');
    this.d.pages.showHome({
      continueLabel,
      cover: entry.cover,
      coverTitle: entry.title,
      daily: { done: !!this.save.daily.results[t], streak: currentStreak(this.save, t) },
      tarot,
    });
  }

  showCities(): void {
    const locks = cityLocks(this.save, this.gates);
    this.enterDom('cities', '选择城市');
    this.d.pages.showCities(
      // 月亮塔罗有自己的首页入口，不在城市列表里
      this.d.themes.flatMap((t, i) => {
        if (this.isTarot(t.id)) return [];
        const m = this.byTheme.get(t.id)!.manifest;
        const prev = locks[i].prev;
        return {
          id: t.id,
          title: t.title,
          subtitle: t.subtitle,
          // 小卡片用缩略图（城市第一个地点 / 塔罗第 0 张），不解码整张 1024 大图
          cover: this.byTheme.get(t.id)!.places[0]?.thumb ?? t.cover,
          portrait: imageAspect(m) < 0.9,
          completed: completedCount(this.save, t.id),
          total: this.byTheme.get(t.id)!.places.length,
          open: locks[i].open,
          need: locks[i].need,
          prevTitle: prev ? (this.byTheme.get(prev)?.entry.title ?? null) : null,
        };
      }),
    );
  }

  /** 首页「月亮塔罗」：直接进星图 */
  async openTarot(): Promise<void> {
    const id = this.tarotId;
    if (!id) return;
    this.d.gyro.requestFromGesture();
    return this.openCity(id);
  }

  cityOpen(id: string): boolean {
    const i = this.themeIds.indexOf(id);
    return i >= 0 && cityLocks(this.save, this.gates)[i].open;
  }

  async openCity(id: string): Promise<void> {
    if (!this.cityOpen(id)) {
      this.d.pages.shakeCity(id);
      tapFeedback();
      return;
    }
    this.themeId = id;
    this.save.lastThemeId = id;
    void this.d.store.flush();
    await this.goMap();
  }

  /** 首页"继续"：有进行中的局 → 直接回那一局；否则回上次的城市地图 */
  async continueGame(): Promise<void> {
    this.d.gyro.requestFromGesture();
    const ip = this.liveInProgress();
    if (ip) {
      if (ip.mode === 'journey') {
        this.themeId = ip.themeId;
        this.save.lastThemeId = ip.themeId;
      }
      return this.enterPlay({ mode: ip.mode, themeId: ip.themeId, placeId: ip.placeId, ...gridSpec(inProgressGrid(ip)), date: ip.date ?? null }, ip);
    }
    this.themeId = this.continueTheme();
    await this.goMap();
  }

  private continueTheme(): string {
    const last = this.save.lastThemeId;
    return last && this.cityOpen(last) ? last : this.themeIds[0];
  }

  /** 进行中的局；过期的每日挑战（不是今天的）作废 */
  private liveInProgress(): InProgress | null {
    const ip = this.save.inProgress;
    if (!ip) return null;
    if (ip.mode === 'daily' && ip.date !== today()) {
      delete this.save.inProgress;
      void this.d.store.flush();
      return null;
    }
    return ip;
  }

  todayPuzzle(): DailyPuzzle {
    return dailyPuzzle(today(), this.d.manifests);
  }

  showDaily(): void {
    const t = today();
    const dp = this.todayPuzzle();
    const r = this.save.daily.results[t];
    const ip = this.liveInProgress();
    this.enterDom('daily', '每日挑战');
    this.d.pages.showDaily({
      date: t,
      placeTitle: dp.place.title,
      themeTitle: this.byTheme.get(dp.themeId)!.entry.title,
      image: dp.place.thumb,
      n: dp.n,
      grid: dp.grid,
      mods: dp.mods,
      result: r ? { timeMs: r.bestTimeMs, moves: r.bestMoves, stars: r.stars ?? null } : null,
      hasProgress: ip?.mode === 'daily',
      streak: currentStreak(this.save, t),
      bestStreak: this.save.daily.bestStreak,
      calendar: this.calendar(t),
    });
  }

  /** 最近 4 周（周一开头，最后一行含今天） */
  calendar(t: string): CalendarCell[] {
    const [y, m, d] = t.split('-').map(Number);
    const dow = (new Date(Date.UTC(y, m - 1, d)).getUTCDay() + 6) % 7; // 0 = 周一
    const start = addDays(t, -dow - (CAL_WEEKS - 1) * 7);
    const out: CalendarCell[] = [];
    for (let i = 0; i < CAL_WEEKS * 7; i++) {
      const date = addDays(start, i);
      out.push({ date, day: Number(date.slice(8)), done: !!this.save.daily.results[date], today: date === t, future: date > t });
    }
    return out;
  }

  async playDaily(): Promise<void> {
    this.d.gyro.requestFromGesture();
    if (this.busy) return;
    const dp = this.todayPuzzle();
    const ip = this.liveInProgress();
    if (ip?.mode === 'daily') return this.enterPlay({ mode: 'daily', themeId: ip.themeId, placeId: ip.placeId, ...gridSpec(inProgressGrid(ip)), date: ip.date }, ip);
    if (ip) {
      const ok = await confirmDialog(`放弃『${this.placeOf(ip.themeId, ip.placeId).title}』的进度？`, '放弃并开始', '取消');
      if (!ok) return;
      delete this.save.inProgress;
      void this.d.store.flush();
    }
    return this.enterPlay({ mode: 'daily', themeId: dp.themeId, placeId: dp.place.id, ...gridSpec(dp.grid), seed: dp.seed, date: dp.date }, null);
  }

  showAlbum(tab = this.albumTab): void {
    this.albumTab = this.byTheme.has(tab) ? tab : this.themeIds[0];
    const th = this.byTheme.get(this.albumTab)!;
    const ts = this.themeSave(this.albumTab);
    const theme = th.manifest.theme;
    const cells: AlbumCell[] = th.places.map((p) => {
      const s = ts.places[p.id];
      return {
        chapter: chapterOf(theme, p.unlockOrder)?.title ?? null,
        themeId: this.albumTab,
        placeId: p.id,
        title: p.title,
        state: s.completed ? 'completed' : s.unlocked && this.cityOpen(this.albumTab) ? 'unlocked' : 'locked',
        image: p.thumb,
        teaser: p.teaser,
        catFound: s.catFound,
        stars: s.stars ?? 0,
      };
    });
    const totals = this.totals();
    const withRunes = th.places.every((p) => p.rune);
    const pz = puzzlesOf(this.albumTab);
    this.enterDom('album', '图鉴');
    this.d.pages.showAlbum({
      tabs: this.d.themes.map((t) => ({ id: t.id, title: t.title })),
      tab: this.albumTab,
      portrait: imageAspect(th.manifest) < 0.9,
      cells,
      books: { done: totals.completed, total: totals.places },
      cats: { found: totals.cats, total: totals.places },
      stars: { got: totalStars(this.save), total: totals.places * 3 },
      runes: withRunes ? th.places.map((p) => ({ icon: p.rune!.icon, name: p.rune!.name, found: !!ts.places[p.id].runeFound })) : undefined,
      book: pz
        ? {
            title: pz.book,
            pages: pz.dials.map((d) => ({ title: d.page.title, text: d.page.text, open: dialSolved(this.save, this.albumTab, d.chapter), need: `解开第${CN_NUM[d.chapter]}章星盘` })),
          }
        : undefined,
    });
  }

  private totals(): { places: number; completed: number; cats: number } {
    let places = 0;
    let completed = 0;
    let cats = 0;
    for (const id of this.themeIds) {
      for (const p of this.byTheme.get(id)!.places) {
        const s = this.themeSave(id).places[p.id];
        places += 1;
        if (s.completed) completed += 1;
        if (s.catFound) cats += 1;
      }
    }
    return { places, completed, cats };
  }

  showSettings(): void {
    const t = today();
    const totals = this.totals();
    // 方形 4..9，再加塔罗的竖版格（按块数排在一起）；成绩键见 levels.gridKey
    const grids: Grid[] = [];
    for (let n = MIN_N; n <= MAX_N; n++) grids.push(square(n));
    for (const g of TAROT_GRIDS) if (!grids.some((x) => gridKey(x) === gridKey(g))) grids.push({ ...g });
    grids.sort((a, b) => a.cols * a.rows - b.cols * b.rows);
    const best = grids.map((g) => {
      const key = gridKey(g);
      let timeMs: number | null = null;
      let moves: number | null = null;
      const take = (b: { bestTimeMs: number; bestMoves: number } | undefined) => {
        if (!b) return;
        timeMs = timeMs === null ? b.bestTimeMs : Math.min(timeMs, b.bestTimeMs);
        moves = moves === null ? b.bestMoves : Math.min(moves, b.bestMoves);
      };
      for (const id of this.themeIds) for (const s of Object.values(this.themeSave(id).places)) take(s.best[key]);
      for (const r of Object.values(this.save.daily.results)) if (r.n !== undefined && gridKey({ cols: r.n, rows: r.rows ?? r.n }) === key) take(r);
      return { key, label: gridLabel(g), timeMs, moves };
    });
    const top = this.page === 'settings' ? document.querySelector('#pages')?.scrollTop ?? 0 : 0;
    this.enterDom('settings', '设置');
    this.d.pages.showSettings({
      haptics: this.save.settings.haptics,
      gyro: this.save.settings.gyro,
      version: this.d.version,
      stats: {
        puzzlesCompleted: this.save.stats.puzzlesCompleted,
        totalPlayMs: this.save.stats.totalPlayMs,
        cats: { found: totals.cats, total: totals.places },
        stars: { got: totalStars(this.save), total: totals.places * 3 },
        streak: currentStreak(this.save, t),
        bestStreak: this.save.daily.bestStreak,
        best,
      },
    });
    const root = document.querySelector('#pages');
    if (root) root.scrollTop = top;
  }

  setHaptics(on: boolean): void {
    this.save.settings.haptics = on;
    this.applySettings();
    void this.d.store.flush();
    this.showSettings();
  }

  setGyroSetting(on: boolean): void {
    this.save.settings.gyro = on;
    this.applySettings();
    void this.d.store.flush();
    this.showSettings();
  }

  /** 重置存档：二次确认 → 清空 → 回到首页 */
  async resetSave(): Promise<void> {
    if (!(await confirmDialog('重置存档？全部进度、成绩和打卡都会清空。', '继续', '取消'))) return;
    if (!(await confirmDialog('真的要清空吗？这一步无法撤销。', '清空存档', '取消'))) return;
    await this.d.store.reset();
    this.themeId = this.themeIds[0];
    this.albumTab = this.themeId;
    this.reveal = null;
    this.cityOpened = null;
    this.applySettings();
    this.d.mapUi.toast('存档已重置');
    this.showHome();
  }

  // ── 地图 ──

  async showMap(): Promise<void> {
    const token = ++this.seq;
    if (this.mapTheme !== this.themeId) {
      this.disposeMap();
      this.disposeTown();
    }
    if (this.townIs3D) {
      this.disposeMap();
      if (!this.town) await this.buildTown();
      if (token !== this.seq) return;
      this.town!.showIdle();
      this.enterMapPage(MAX_TILT_DEG);
      return;
    }
    if (!this.map) {
      const m = this.manifest;
      const tex = await loadTexture(this.d.renderer, m.map.image);
      if (token !== this.seq) {
        tex.dispose();
        return;
      }
      const states: Record<string, HotspotState> = {};
      for (const p of this.places) states[p.id] = this.stateOf(p.id);
      // 揭纸前先按"通关之前"的样子摆好，再播放
      const rv = this.reveal?.themeId === this.themeId ? this.reveal : null;
      if (rv) {
        states[rv.completed] = 'unlocked';
        if (rv.unlocked) states[rv.unlocked] = 'locked';
      }
      const ur = this.unlockReveal?.themeId === this.themeId ? this.unlockReveal.ids : [];
      for (const id of ur) states[id] = 'locked';
      const [w, h] = m.map.size;
      this.map = new MapScene(tex, w / h, this.places, states, this.d.glowTexture, m.map.style === 'night' ? 'night' : 'paper');
      this.mapTheme = this.themeId;
      this.map.setViewport(this.vp);
      if (rv) {
        this.map.setState(rv.completed, 'completed', true);
        if (rv.unlocked) this.map.setState(rv.unlocked, 'unlocked', true);
      }
      for (const id of ur) this.map.setState(id, 'unlocked', true);
    }
    this.reveal = null;
    this.unlockReveal = null;
    this.enterMapPage(MAP_MAX_TILT);
    if (this.endingReady && !this.themeSave().endingSeen) this.endingIn = ENDING_DELAY_MS;
    if (this.cityOpened) {
      this.d.mapUi.toast(`${this.byTheme.get(this.cityOpened)?.entry.title ?? ''}开放了！`);
      this.cityOpened = null;
    }
  }

  private enterMapPage(maxTilt: number): void {
    this.d.hud.setTitle(this.byTheme.get(this.themeId)!.entry.title);
    this.d.hud.setPopup(false);
    this.d.hud.hideResult();
    this.updateLit();
    this.setPage('map');
    this.d.gyro.setActive(true, maxTilt);
  }

  private disposeMap(): void {
    this.map?.dispose();
    this.map = null;
    this.mapTheme = this.town ? this.mapTheme : null;
    this.d.mapUi.setTeasers([]);
    this.d.mapUi.setDials([]);
  }

  private disposeTown(): void {
    this.town?.dispose();
    this.town = null;
    this.townLayers = null;
    if (!this.map) this.mapTheme = null;
  }

  private updateLit(): void {
    this.d.mapUi.setLit(this.places.filter((p) => this.themeSave().places[p.id]?.completed).length, this.places.length);
  }

  private async buildTown(): Promise<void> {
    const m = this.manifest;
    const { layers } = await loadLayers(this.d.renderer, m.map.image, m.map.layers.map((l) => l.file));
    const [w, h] = m.map.size;
    const aspect = w / h;
    // 地图没有猫：放一个点不中的占位
    const town = new PopupScene(layers, { x: -10, y: -10, r: 0.001, layer: 0 }, { rectFor: (vp) => mapRect(vp, aspect), aspect });
    town.setMarkers(
      this.places.map((p) => ({ x: p.hotspot.x, y: p.hotspot.y, r: p.hotspot.r, layer: Math.min(p.hotspot.layer, layers.length - 1) })),
      this.d.glowTexture,
    );
    town.setViewport(this.vp);
    this.town = town;
    this.townLayers = layers;
    this.mapTheme = this.themeId;
  }

  private async startEnding(): Promise<void> {
    if (this.busy) return;
    this.busy = true;
    try {
      this.disposeTown();
      await this.buildTown();
      this.disposeMap();
      this.endingStoryShown = false;
      this.town!.start(HANDOVER_MS);
      this.setPage('ending');
      this.d.gyro.setActive(true, MAX_TILT_DEG);
    } finally {
      this.busy = false;
    }
  }

  private finishEnding(): void {
    this.themeSave().endingSeen = true;
    void this.d.store.flush();
    this.d.mapUi.hideEnding();
    this.town?.showIdle();
    this.d.hud.setTitle(this.byTheme.get(this.themeId)!.entry.title);
    this.setPage('map');
    this.d.gyro.setActive(true, MAX_TILT_DEG);
  }

  private mapTap(x: number, y: number): void {
    let hit: { id: string; state: HotspotState } | null = null;
    if (this.town && this.page === 'map') hit = this.townHit(x, y);
    else if (this.map) hit = this.map.hit(x, y);
    this.lastMapTap = { id: hit?.id ?? null, state: hit?.state ?? null };
    if (!hit) return;
    if (hit.state === 'locked') {
      this.map?.shake(hit.id);
      tapFeedback();
      return;
    }
    this.openCard(hit.id);
  }

  /** 终章后的立体地图：射线投到 hotspot.layer 平面上判定 */
  private townHit(x: number, y: number): { id: string; state: HotspotState } | null {
    const town = this.town!;
    let best: { id: string; state: HotspotState; d: number } | null = null;
    for (const p of this.places) {
      const k = Math.min(p.hotspot.layer, (this.townLayers?.length ?? 1) - 1);
      const q = town.pointOnLayer(x, y, k);
      if (!q) continue;
      const [w, h] = this.manifest.map.size;
      const d = Math.hypot(q.x - p.hotspot.x, (q.y - p.hotspot.y) * (h / w));
      const r = Math.max(p.hotspot.r, 22 / town.pxPerUnitAt(k, p.hotspot.x, p.hotspot.y));
      if (d <= r && (!best || d < best.d)) best = { id: p.id, state: this.stateOf(p.id), d };
    }
    return best;
  }

  /** 热点在屏幕上的位置（测试 / 标签用） */
  hotspotScreen(id: string): { x: number; y: number; r: number } | null {
    if (this.map) return this.map.hotspotScreen(id);
    const p = this.places.find((q) => q.id === id);
    if (!this.town || !p) return null;
    const k = Math.min(p.hotspot.layer, (this.townLayers?.length ?? 1) - 1);
    const s = this.town.screenOf(k, p.hotspot.x, p.hotspot.y);
    return { ...s, r: p.hotspot.r * this.town.pxPerUnitAt(k, p.hotspot.x, p.hotspot.y) };
  }

  // ── 地点卡 ──

  /** 这个地点的格（关卡表，见 core/levels）：塔罗查自己的表；城市按第几座城 + 城内顺序 */
  gridOf(themeId: string, placeId: string): Grid {
    return levelGrid(themeId, this.themeIds.indexOf(themeId), this.placeOf(themeId, placeId).unlockOrder);
  }

  /** 方形关卡的边长（测试 / 旧接口）；长方形时是列数 */
  sizeOf(themeId: string, placeId: string): number {
    return this.gridOf(themeId, placeId).cols;
  }

  /** 这一关的玩法（关卡表，见 core/levels）：旋转块 / 背面块 */
  modsOf(themeId: string, placeId: string): LevelMods {
    return themeLevelMods(themeId, this.themeIds.indexOf(themeId), this.placeOf(themeId, placeId).unlockOrder);
  }

  openCard(id: string): void {
    this.setPage('card');
    this.card.show(this.cardModel(id));
  }

  private cardModel(id: string): CardModel {
    const p = this.places.find((q) => q.id === id)!;
    const s = this.themeSave().places[id];
    const grid = this.gridOf(this.themeId, id);
    const ip = this.save.inProgress;
    const mine = ip && ip.mode === 'journey' && ip.themeId === this.themeId && ip.placeId === id;
    return {
      placeId: id,
      title: p.title,
      chapter: chapterOf(this.manifest.theme, p.unlockOrder)?.title ?? null,
      text: s.completed ? p.story : p.teaser,
      completed: s.completed,
      n: grid.cols,
      grid,
      mods: this.modsOf(this.themeId, id),
      stars: s.stars ?? 0,
      best: s.best[gridKey(grid)] ?? null,
      resume: mine ? { n: ip.n, grid: inProgressGrid(ip) } : null,
      catFound: s.catFound,
      runeFound: p.rune ? !!s.runeFound : null,
      status: 'idle',
    };
  }

  closeCard(): void {
    if (this.page !== 'card') return;
    this.card.hide();
    this.setPage('map');
  }

  /** fresh = 开始 / 重新开始（按选中的难度）；否则 = 继续（按存档的 n） */
  private async startFromCard(fresh: boolean): Promise<void> {
    // iOS 的陀螺仪权限只能在用户手势里申请：第一次点"开始"（spec §6）
    this.d.gyro.requestFromGesture();
    const m = this.card.model;
    if (!m || this.busy) return;
    const ip = this.save.inProgress;
    const mine = ip && ip.mode === 'journey' && ip.themeId === this.themeId && ip.placeId === m.placeId;
    const spec = { mode: 'journey' as const, themeId: this.themeId, placeId: m.placeId };
    if (!fresh) {
      if (!mine) return;
      return this.enterPlay({ ...spec, ...gridSpec(inProgressGrid(ip)) }, ip);
    }
    if (ip && !mine) {
      const other = ip.mode === 'daily' ? '每日挑战' : this.placeOf(ip.themeId, ip.placeId).title;
      const ok = await confirmDialog(`放弃『${other}』的进度？`, '放弃并开始', '取消');
      if (!ok) return;
      delete this.save.inProgress;
      void this.d.store.flush();
    }
    return this.enterPlay({ ...spec, ...gridSpec(m.grid ?? square(m.n)) }, null);
  }

  // ── 拼图（旅程与每日挑战共用 PlaySession / BoardScene） ──

  async enterPlay(spec: PlaySpec, resume: InProgress | null, opts: { onboarding?: boolean } = {}): Promise<void> {
    if (this.busy) return;
    this.busy = true;
    const place = this.placeOf(spec.themeId, spec.placeId);
    const daily = spec.mode === 'daily';
    this.card.update({ status: 'loading' });
    let layers: PopupLayer[] | null = null;
    let picture: THREE.Texture;
    try {
      if (daily) picture = await loadTexture(this.d.renderer, place.image);
      else {
        const loaded = await loadLayers(this.d.renderer, place.image, place.layers.map((l) => l.file));
        layers = loaded.layers;
        picture = loaded.picture;
      }
    } catch (err) {
      console.warn('place failed to load', err);
      this.card.update({ status: 'error' });
      if (this.page !== 'card') this.d.mapUi.toast('内容加载失败，请重试');
      this.busy = false;
      return;
    }
    this.disposePlace();
    this.disposeMap();
    this.disposeTown();
    const hud = this.d.hud;
    const board = new BoardScene(picture);
    const seed = resume?.seed ?? spec.seed ?? this.d.seed ?? (Math.floor(Math.random() * 2 ** 30) + 1);
    const mods = daily ? dailyMods(spec.date ?? today()) : this.modsOf(spec.themeId, spec.placeId);
    const session = new PlaySession(board, hud, specGrid(spec), seed, mods);
    session.fixedSeed = daily;
    let popup: PopupScene | null = null;
    if (layers) {
      const cat = { ...place.cat, layer: Math.min(place.cat.layer, layers.length - 1) };
      popup = new PopupScene(layers, cat, { aspect: board.aspect, rune: place.rune, runeFound: !!this.themeSave(spec.themeId).places[place.id]?.runeFound });
    }
    this.countedMs = 0;
    const res: PlayRes = { mode: spec.mode, themeId: spec.themeId, date: spec.date ?? null, place, board, session, popup, result: null, daily: null, onboardDrag: false };
    this.play = res;
    session.setViewport(this.vp);
    popup?.setViewport(this.vp);
    if (resume) session.restore(resume.board, resume.seed, resume.moves, resume.elapsedMs, resume.par);
    session.onChange = () => this.onBoardChange();
    session.onMerge = () => this.onMerge();
    session.onComplete = () => this.onComplete();
    session.paused = document.hidden;
    this.saveProgress();
    this.card.hide();
    hud.setTitle(daily ? '每日挑战' : place.title);
    hud.setPopup(false);
    hud.hideResult();
    this.setDailyDone(false);
    this.resultShown = false;
    this.lastTap = null;
    this.d.gyro.setActive(false);
    this.setPage('play');
    this.busy = false;
    // 新手引导（只在旅程里）优先；它没有占用画面时，再看要不要讲这一关的新玩法
    if (opts.onboarding !== false && !daily) this.startOnboarding();
    if (opts.onboarding !== false && this.d.onboarding.stage === 'none') this.maybeIntro();
  }

  /** 这一关有还没讲过的新玩法：讲一次（锁住棋盘、不计时），看过记进存档 intros */
  private maybeIntro(): void {
    const play = this.play;
    if (!play || play.session.completed) return;
    const s = play.session;
    const seen = this.save.intros ?? {};
    const kind: IntroKind | null = s.mods.rotate && !seen.rotate ? 'rotate' : s.mods.flip && !seen.flip ? 'flip' : null;
    if (!kind) return;
    // 示例块：离棋盘中心最近的一个旋转块 / 背面块（没有了就不圈）
    const b = s.board;
    const N = tileCount(b);
    const midC = (b.n - 1) / 2;
    const midR = (rowsOf(b) - 1) / 2;
    let pick = -1;
    let best = Infinity;
    for (let t = 0; t < N; t++) {
      const ok = kind === 'rotate' ? canRotate(b, t) : !!b.down?.[t];
      if (!ok) continue;
      const d = Math.hypot(b.pos[t].row - midR, b.pos[t].col - midC);
      if (d < best) {
        best = d;
        pick = t;
      }
    }
    let tile: { x: number; y: number; size: number } | null = null;
    if (pick >= 0) {
      const c = cellCenter(play.board.boardRect, s.grid, b.pos[pick]);
      const r = play.board.boardRect;
      tile = { ...worldToScreen(this.vp, c.x, c.y), size: Math.min(r.w / s.n, r.h / s.rows) };
    }
    s.locked = true;
    this.d.onboarding.showIntro(kind, tile, () => {
      if (this.play === play) play.session.locked = false;
      this.save.intros = { ...(this.save.intros ?? {}), [kind]: true };
      void this.d.store.flush();
      // 同一关两种玩法都开着（表里没有，但允许）：接着讲第二个
      if (this.play === play) this.maybeIntro();
    });
  }

  private setDailyDone(on: boolean): void {
    document.querySelector('#app')!.classList.toggle('is-daily-done', on);
  }

  private startOnboarding(): void {
    const play = this.play!;
    const step = this.save.onboarding.step;
    if (step === 0) {
      const h = play.session.hintMove();
      if (!h) return;
      play.onboardDrag = true;
      const { board, grid } = play.session;
      const r = play.board.boardRect;
      const from = cellCenter(r, grid, board.pos[h.anchor]);
      const to = cellCenter(r, grid, h.to);
      this.d.onboarding.showDrag(worldToScreen(this.vp, from.x, from.y), worldToScreen(this.vp, to.x, to.y));
    } else if (step === 1) {
      play.session.locked = true;
      this.d.onboarding.showTools(() => {
        if (this.play === play) play.session.locked = false;
        this.save.onboarding.step = 2;
        void this.d.store.flush();
        if (this.play === play) this.maybeIntro();
      });
    }
  }

  private onBoardChange(): void {
    if (this.page !== 'play') return;
    this.d.onboarding.dragDone();
    this.saveProgress();
  }

  private onMerge(): void {
    const play = this.play;
    if (!play || !play.onboardDrag || this.save.onboarding.step !== 0) return;
    play.onboardDrag = false;
    play.session.locked = true;
    this.d.onboarding.showMerge(() => {
      if (this.play === play) play.session.locked = false;
      this.save.onboarding.step = 1;
      void this.d.store.flush();
    });
  }

  /** 把本局新增的实际计时计入"总用时"（离开拼图页、进后台、通关、每步存档时） */
  private commitPlayTime(): void {
    const s = this.play?.session;
    if (!s) return;
    const add = Math.round(s.playedMs - this.countedMs);
    if (add <= 0) return;
    this.save.stats.totalPlayMs += add;
    this.countedMs += add;
  }

  /** 单槽 inProgress：每走一步、返回、进后台时写入 */
  private saveProgress(): void {
    const play = this.play;
    if (!play) return;
    this.commitPlayTime();
    if (play.session.completed) return;
    const s = play.session;
    this.save.inProgress = {
      mode: play.mode,
      themeId: play.themeId,
      placeId: play.place.id,
      ...(play.date ? { date: play.date } : {}),
      n: s.n,
      ...(s.rows !== s.n ? { rows: s.rows } : {}),
      seed: s.seed,
      board: {
        n: s.board.n,
        ...(s.board.rows !== undefined ? { rows: s.board.rows } : {}),
        pos: s.board.pos.map((c) => ({ row: c.row, col: c.col })),
        ...(s.board.rot ? { rot: [...s.board.rot] } : {}),
        ...(s.board.down ? { down: [...s.board.down] } : {}),
      },
      moves: s.moves,
      elapsedMs: Math.round(s.elapsedMs),
      par: { ...s.par },
    };
    void this.d.store.flush();
  }

  private onComplete(): void {
    const play = this.play!;
    const s = play.session;
    if (play.onboardDrag && this.save.onboarding.step === 0) this.save.onboarding.step = 1;
    this.d.onboarding.hide();
    s.locked = false;
    this.commitPlayTime();
    if (play.mode === 'daily') {
      // 每日挑战：不影响旅程进度，不弹立体书，棋盘上直接出结算条
      play.daily = recordDaily(this.save, play.date!, play.themeId, play.place.id, s.grid, s.elapsedMs, s.moves, s.stars ?? undefined);
      void this.d.store.flush();
      this.setDailyDone(true);
      this.resultShown = true;
      this.d.hud.showDailyResult({ timeMs: s.elapsedMs, moves: s.moves, first: play.daily.first, streak: play.daily.streak, stars: s.stars });
      return;
    }
    const before = cityLocks(this.save, this.gates);
    const m = this.byTheme.get(play.themeId)!.manifest;
    const r = recordCompletion(this.save, m, play.place.id, s.grid, s.elapsedMs, s.moves, s.stars ?? undefined);
    play.result = r;
    if (r.firstClear) this.reveal = { themeId: play.themeId, completed: play.place.id, unlocked: r.unlockedId };
    const after = cityLocks(this.save, this.gates);
    const opened = after.findIndex((l, i) => l.open && !before[i].open);
    if (opened >= 0) this.cityOpened = this.themeIds[opened];
    void this.d.store.flush();
    this.setPage('popup');
    this.resultShown = false;
    play.popup!.start();
    this.d.gyro.setActive(true, MAX_TILT_DEG);
    this.d.hud.setPopup(true);
  }

  // ── 查看立体书（地点卡 / 图鉴） ──

  async enterView(themeId: string, placeId: string, from: 'map' | 'album'): Promise<void> {
    if (this.busy) return;
    this.busy = true;
    const place = this.placeOf(themeId, placeId);
    this.card.update({ status: 'loading' });
    let layers: PopupLayer[];
    try {
      layers = (await loadLayers(this.d.renderer, place.image, place.layers.map((l) => l.file))).layers;
    } catch (err) {
      console.warn('place failed to load', err);
      this.card.update({ status: 'error' });
      if (from === 'album') this.d.mapUi.toast('内容加载失败，请重试');
      this.busy = false;
      return;
    }
    this.disposePlace();
    this.disposeMap();
    this.disposeTown();
    const cat = { ...place.cat, layer: Math.min(place.cat.layer, layers.length - 1) };
    const popup = new PopupScene(layers, cat, { aspect: imageAspect(this.byTheme.get(themeId)!.manifest), rune: place.rune, runeFound: !!this.themeSave(themeId).places[place.id]?.runeFound });
    popup.setViewport(this.vp);
    popup.start(HANDOVER_MS);
    this.view = { themeId, place, layers, popup, from };
    this.card.hide();
    this.d.hud.setTitle(place.title);
    this.d.hud.setPopup(true);
    this.d.hud.setStory(place.story);
    this.resultShown = false;
    this.lastTap = null;
    this.d.gyro.setActive(true, MAX_TILT_DEG);
    this.setPage('viewPopup');
    this.busy = false;
  }

  // ── 离开 ──

  private disposePlace(): void {
    if (this.play) {
      this.commitPlayTime();
      void this.d.store.flush();
      this.play.board.dispose();
      this.play.popup?.dispose();
      this.play = null;
    }
    if (this.view) {
      this.view.popup.dispose();
      this.view = null;
    }
    this.setDailyDone(false);
    this.d.onboarding.hide();
    this.d.hud.hideResult();
    this.d.hud.setStory(null);
  }

  /**
   * 返回上一级（左上按钮 / 浏览器后退 / Android 返回键共用）：
   * 城市选择·每日·图鉴·设置 → 首页；地图 → 城市选择；地点卡 → 地图；
   * 拼图（旅程）·立体书 → 地图；拼图（每日）→ 每日挑战；从图鉴打开的立体书 → 图鉴；终章 → 地图。
   * 有确认框时只关掉确认框。
   */
  async back(): Promise<void> {
    if (cancelConfirm()) return;
    if (this.busy) return;
    switch (this.page) {
      case 'home':
        return;
      case 'cities':
      case 'daily':
      case 'album':
      case 'settings':
        return this.showHome();
      case 'map':
        // 月亮塔罗从首页进来，回首页；城市回城市选择
        return this.isTarot() ? this.showHome() : this.showCities();
      case 'dial':
        return this.goMap();
      case 'card':
        if (this.card.model?.status === 'loading') return;
        return this.closeCard();
      case 'play':
        this.saveProgress();
        if (this.play?.mode === 'daily') return this.showDaily();
        return this.goMap();
      case 'popup':
        return this.goMap();
      case 'viewPopup':
        if (this.view?.from === 'album') return this.showAlbum(this.view.themeId);
        return this.goMap();
      case 'ending':
        if (this.town) this.finishEnding();
        return;
    }
  }

  /** 结算条上的按钮：每日挑战 → 回每日挑战页；图鉴里回看 → 回图鉴；否则回地图 */
  async resultAction(): Promise<void> {
    if (this.play?.mode === 'daily') return this.showDaily();
    if (this.page === 'viewPopup' && this.view?.from === 'album') return this.showAlbum(this.view.themeId);
    return this.goMap();
  }

  /** 回到当前城市的地图 */
  async goMap(): Promise<void> {
    if (this.busy) return;
    this.busy = true;
    try {
      // 先把地图载好再释放拼图资源：载入期间旧页面照常渲染，不会出现空帧
      this.d.gyro.setActive(false);
      this.card.hide();
      await this.showMap();
      this.disposePlace();
    } finally {
      this.busy = false;
    }
  }

  /** 进后台：暂停计时并存档（进程可能被杀） */
  setHidden(hidden: boolean): void {
    if (this.play) this.play.session.paused = hidden;
    if (hidden && this.play) this.saveProgress();
  }

  // ── 每帧 ──

  update(dtMs: number): void {
    this.dayNight.update(dtMs, this.hour());
    this.d.onboarding.update(dtMs);
    const gyro = this.d.gyro;
    const grade = this.dayNight.cur;
    switch (this.page) {
      case 'map':
      case 'card':
        gyro.update(dtMs);
        if (this.map) {
          this.map.update(dtMs, gyro.tilt, grade);
          this.updateTeasers();
          if (this.endingIn >= 0 && !this.map.animating && this.page === 'map') {
            this.endingIn -= dtMs;
            if (this.endingIn < 0) void this.startEnding();
          }
        }
        if (this.town) {
          this.town.setGrade(grade.tint, grade.bright);
          this.town.setMarkerStrength(grade.glow);
          this.town.update(dtMs, gyro.tilt);
        }
        break;
      case 'play':
        this.play?.session.update(dtMs);
        break;
      case 'popup': {
        const play = this.play;
        if (!play?.popup) break;
        play.session.update(dtMs);
        gyro.update(dtMs);
        play.popup.update(dtMs, gyro.tilt);
        if (!this.resultShown && play.popup.phase === 'idle') {
          this.resultShown = true;
          this.d.hud.showResult({
            timeMs: play.session.elapsedMs,
            moves: play.session.moves,
            catFound: play.popup.catFound,
            rune: play.place.rune ? play.popup.runeFound : null,
            newBestTime: play.result?.newBestTime,
            newBestMoves: play.result?.newBestMoves,
            stars: play.session.stars,
          });
        }
        break;
      }
      case 'viewPopup': {
        const v = this.view;
        if (!v) break;
        gyro.update(dtMs);
        v.popup.update(dtMs, gyro.tilt);
        if (!this.resultShown && v.popup.phase === 'idle') {
          this.resultShown = true;
          const ps = this.themeSave(v.themeId).places[v.place.id];
          this.d.hud.showView(ps.catFound || v.popup.catFound, v.from === 'album' ? '回到图鉴' : '回到地图', v.place.rune ? !!ps.runeFound || v.popup.runeFound : null);
        }
        break;
      }
      case 'ending': {
        const town = this.town;
        if (!town) break;
        gyro.update(dtMs);
        town.setGrade(grade.tint, grade.bright);
        town.setMarkerStrength(grade.glow);
        town.update(dtMs, gyro.tilt);
        if (!this.endingStoryShown && town.phase === 'idle') {
          this.endingStoryShown = true;
          this.themeSave().endingSeen = true;
          void this.d.store.flush();
          this.d.mapUi.showEnding(this.manifest.theme.endingStory, () => this.finishEnding());
        }
        break;
      }
      default:
        break;
    }
  }

  /** 标签可用的竖直范围：头部布条之下、底部"已点亮"之上（只在尺寸变化时重新量） */
  private labelBounds: { key: string; top: number; bottom: number } | null = null;
  private mapLabelBounds(): { top: number; bottom: number } {
    const key = `${this.vp.width}x${this.vp.height}`;
    if (this.labelBounds?.key !== key) {
      // 毛毡顶栏（.ui-header）比中间的标题牌更低：以它的下沿为界，标签不会钻到顶栏底下
      const plaque = document.querySelector('.hud-top .ui-plaque')?.getBoundingClientRect();
      const felt = document.querySelector('.hud-top .ui-header')?.getBoundingClientRect();
      const head = felt && plaque ? (felt.bottom > plaque.bottom ? felt : plaque) : (felt ?? plaque);
      const foot = document.querySelector('#map-footer .ui-pill')?.getBoundingClientRect();
      this.labelBounds = { key, top: head?.bottom ?? 0, bottom: foot && foot.height > 0 ? foot.top : this.vp.height };
    }
    return this.labelBounds;
  }

  teaserLayout() {
    return this.d.mapUi.lastLayout;
  }

  private updateTeasers(): void {
    const map = this.map!;
    const list: TeaserLabel[] = [];
    const obstacles = [];
    for (const p of this.places) {
      const s = map.hotspotScreen(p.id)!;
      obstacles.push({ x: s.x, y: s.y, r: Math.max(s.r, 22) });
      if (map.stateOf(p.id) !== 'unlocked') continue;
      list.push({ id: p.id, text: p.teaser, x: s.x, y: s.y, r: Math.max(s.r, 22) });
    }
    // 章节标题（塔罗），排在提示之后（提示优先占好位置）。第一章贴在第一颗星旁；
    // 之后各章贴在两章之间那段虚线的中点（章节分界处），不会被挤到上一章的星星堆里
    // 星盘锁：前一章的星盘没解开，这一章的标题带锁和一行说明
    const dials = this.dialMarkers();
    // 星盘按钮 + 下面的「星盘」小字都不让标签压住
    for (const d of dials) obstacles.push({ x: d.x, y: d.y, r: 30 }, { x: d.x, y: d.y + 38, r: 20 });
    (this.manifest.theme.chapters ?? []).forEach((c, i) => {
      const first = this.places.find((p) => p.unlockOrder === c.from);
      const s = first && map.hotspotScreen(first.id);
      if (!s) return;
      const locked = i > 0 && !!dialOf(this.themeId, i - 1) && !dialSolved(this.save, this.themeId, i - 1);
      const text = `第${CN_NUM[i] ?? i + 1}章 · ${c.title}${locked ? `\n解开第${CN_NUM[i - 1]}章的星盘` : ''}`;
      const prev = i > 0 ? this.places.find((p) => p.unlockOrder === c.from - 1) : undefined;
      const q = prev && map.hotspotScreen(prev.id);
      if (q) list.push({ id: `chapter-${i}`, kind: 'chapter', locked, text, x: (s.x + q.x) / 2, y: (s.y + q.y) / 2, r: 4 });
      else list.push({ id: `chapter-${i}`, kind: 'chapter', locked, text, x: s.x, y: s.y, r: Math.max(s.r, 22) });
    });
    this.d.mapUi.setTeasers(list, obstacles, this.mapLabelBounds());
    this.d.mapUi.setDials(dials);
  }

  /** 章节全部通关后，星盘出现在这一章星座的末尾（顺着最后一段连线再往外一点） */
  private dialMarkers(): DialMarker[] {
    const map = this.map;
    const pz = puzzlesOf(this.themeId);
    if (!map || !pz) return [];
    const out: DialMarker[] = [];
    const b = this.mapLabelBounds();
    for (const d of pz.dials) {
      const c = this.manifest.theme.chapters?.[d.chapter];
      if (!c || !this.chapterDone(d.chapter)) continue;
      const last = this.places.find((p) => p.unlockOrder === c.to);
      const prev = this.places.find((p) => p.unlockOrder === c.to - 1);
      const s = last && map.hotspotScreen(last.id);
      const q = prev && map.hotspotScreen(prev.id);
      if (!s) continue;
      let dx = q ? s.x - q.x : 1;
      let dy = q ? s.y - q.y : 0;
      const len = Math.hypot(dx, dy) || 1;
      dx /= len;
      dy /= len;
      const k = Math.max(s.r, 22) + 32;
      const x = Math.max(30, Math.min(this.vp.width - 30, s.x + dx * k));
      const y = Math.max(b.top + 30, Math.min(b.bottom - 30, s.y + dy * k));
      out.push({ chapter: d.chapter, x, y, state: dialSolved(this.save, this.themeId, d.chapter) ? 'solved' : 'ready' });
    }
    return out;
  }

  /** 这一章的牌全部通关 */
  private chapterDone(chapter: number, themeId = this.themeId): boolean {
    const c = this.byTheme.get(themeId)?.manifest.theme.chapters?.[chapter];
    if (!c) return false;
    const ts = this.themeSave(themeId);
    return this.byTheme.get(themeId)!.places.filter((p) => p.unlockOrder >= c.from && p.unlockOrder <= c.to).every((p) => ts.places[p.id]?.completed);
  }

  // ── 星盘锁（月亮塔罗）──

  /** 地图上点星盘：打开谜题页（已解开的直接显示那一页书） */
  openDial(chapter: number): void {
    if (this.page !== 'map' || this.busy) return;
    const themeId = this.themeId;
    const dial = dialOf(themeId, chapter);
    const pz = puzzlesOf(themeId);
    const c = this.manifest.theme.chapters?.[chapter];
    if (!dial || !pz || !c || !this.chapterDone(chapter)) return;
    tapFeedback();
    const ts = this.themeSave(themeId);
    const inChapter = this.places.filter((p) => p.unlockOrder >= c.from && p.unlockOrder <= c.to && p.rune);
    const runes = inChapter.map((p) => ({ id: p.id, name: p.rune!.name, icon: p.rune!.icon, found: !!ts.places[p.id]?.runeFound, card: p.title }));
    const found = new Set(runes.filter((r) => r.found).map((r) => r.id));
    const ds = dialSave(this.save, themeId, chapter);
    const n = dial.answer.length;
    // 存档里的格子：长度对齐、只留本章已找到的符文、不重复
    const seen = new Set<string>();
    const slots = Array.from({ length: n }, (_, i) => {
      const id = ds.slots?.[i] ?? null;
      if (!id || !found.has(id) || seen.has(id)) return null;
      seen.add(id);
      return id;
    });
    const solved = !!ds.solved;
    const model: DialModel = {
      title: dial.title,
      chapter: `第${CN_NUM[chapter]}章 · ${c.title}`,
      poem: dial.poem,
      runes,
      slots: solved ? [...dial.answer] : slots,
      hinted: solved ? [] : dial.answer.slice(0, Math.min(ds.hints ?? 0, n - 1)),
      wrong: ds.wrong ?? 0,
      solved,
      page: { book: pz.book, title: dial.page.title, text: dial.page.text },
      last: chapter === pz.dials.length - 1,
    };
    const view = new DialView(model, {
      change: (sl) => {
        ds.slots = sl;
        void this.d.store.flush();
      },
      check: (ids) => this.checkDial(themeId, chapter, ids),
      hint: () => {
        if ((ds.wrong ?? 0) < 3 || ds.solved) return null;
        ds.hints = Math.min(n - 1, (ds.hints ?? 0) + 1);
        void this.d.store.flush();
        return dial.answer.slice(0, ds.hints);
      },
      missing: () => tapFeedback(),
      close: () => void this.goMap(),
    });
    this.enterDom('dial', dial.title);
    this.dialView = view;
    this.dialChapter = chapter;
    this.d.pages.mount('dial', view.root);
  }

  /** 转动星盘：对 = 记账、开放下一章（回地图时播放解锁）；错 = 记一次 */
  private checkDial(themeId: string, chapter: number, ids: string[]): boolean {
    const dial = dialOf(themeId, chapter)!;
    const ds = dialSave(this.save, themeId, chapter);
    const ok = ids.length === dial.answer.length && ids.every((id, i) => id === dial.answer[i]);
    if (!ok) {
      ds.wrong = (ds.wrong ?? 0) + 1;
      tapFeedback();
      void this.d.store.flush();
      return false;
    }
    ds.solved = true;
    ds.slots = [...ids];
    const th = this.byTheme.get(themeId)!;
    const ts = this.themeSave(themeId);
    const before = new Set(th.places.filter((p) => ts.places[p.id].unlocked).map((p) => p.id));
    reconcile(this.save, [th.manifest]);
    const opened = th.places.filter((p) => ts.places[p.id].unlocked && !before.has(p.id)).map((p) => p.id);
    if (opened.length) this.unlockReveal = { themeId, ids: opened };
    // 最后一章的星盘：终章（就算以前看过，也再走一遍——这次它是谜底）
    if (allDialsSolved(this.save, themeId) && this.allCompleted) ts.endingSeen = false;
    void this.d.store.flush();
    return true;
  }

  render(): void {
    const r = this.d.renderer;
    switch (this.page) {
      case 'map':
      case 'card':
        if (this.town) r.render(this.town.scene, this.town.camera);
        else if (this.map) r.render(this.map.scene, this.map.camera);
        else r.clear();
        break;
      case 'play':
        if (this.play) r.render(this.play.board.scene, this.play.board.camera);
        break;
      case 'popup': {
        const p = this.play;
        if (!p?.popup) break;
        if (p.popup.wantsBoard) r.render(p.board.scene, p.board.camera);
        else r.render(p.popup.scene, p.popup.camera);
        break;
      }
      case 'viewPopup':
        if (this.view) r.render(this.view.popup.scene, this.view.popup.camera);
        break;
      case 'ending':
        if (this.town) r.render(this.town.scene, this.town.camera);
        break;
      default:
        r.clear();
    }
  }

  // ── 输入（画布局部 CSS px） ──

  private canvasInput(): boolean {
    return !this.isDomPage(this.page) && this.page !== 'card';
  }

  pointerDown(x: number, y: number, id: number): void {
    if (this.page === 'play') return this.play?.session.pointerDown(x, y, id);
    if (this.gesture || !this.canvasInput()) return;
    this.gesture = { id, x, y, dragging: false };
  }

  pointerMove(x: number, y: number, id: number): void {
    if (this.page === 'play') return this.play?.session.pointerMove(x, y, id);
    const g = this.gesture;
    if (!g || g.id !== id) return;
    if (!g.dragging && Math.hypot(x - g.x, y - g.y) > DRAG_THRESHOLD) {
      g.dragging = true;
      this.d.gyro.dragStart(g.x, g.y);
    }
    if (g.dragging) this.d.gyro.dragMove(x, y);
  }

  pointerUp(x: number, y: number, id: number): void {
    if (this.page === 'play') return this.play?.session.pointerUp(x, y, id);
    const g = this.gesture;
    if (!g || g.id !== id) return;
    this.gesture = null;
    if (g.dragging) return this.d.gyro.dragEnd();
    this.tap(x, y);
  }

  pointerCancel(id: number): void {
    if (this.page === 'play') return this.play?.session.pointerCancel(id);
    if (this.gesture?.id === id) {
      this.gesture = null;
      this.d.gyro.dragEnd();
    }
  }

  tap(x: number, y: number): TapResult | null {
    if (this.page === 'map') {
      this.mapTap(x, y);
      return null;
    }
    const popup = this.page === 'popup' ? this.play?.popup : this.page === 'viewPopup' ? this.view?.popup : null;
    if (!popup) return null;
    const r = popup.tap(x, y);
    this.lastTap = r;
    if (r.kind === 'cat') {
      tapFeedback();
      this.d.hud.setCat(true);
      const owner = this.play ? { themeId: this.play.themeId, id: this.play.place.id } : { themeId: this.view!.themeId, id: this.view!.place.id };
      this.themeSave(owner.themeId).places[owner.id].catFound = true;
      void this.d.store.flush();
    } else if (r.kind === 'rune') {
      tapFeedback();
      const owner = this.play ? { themeId: this.play.themeId, place: this.play.place } : { themeId: this.view!.themeId, place: this.view!.place };
      this.themeSave(owner.themeId).places[owner.place.id].runeFound = true;
      void this.d.store.flush();
      const at = popup.runeScreen() ?? { x, y };
      this.d.hud.flyRune(owner.place.rune!.icon, at.x, at.y);
    }
    return r;
  }

  // ── 测试钩子用 ──

  /** 标记当前城市全部地点通关（终章还没看）并回到地图，地图会自动开始终章 */
  async completeAllForTest(): Promise<void> {
    const ts = this.themeSave();
    for (const p of this.places) {
      ts.places[p.id].unlocked = true;
      ts.places[p.id].completed = true;
    }
    ts.endingSeen = false;
    delete this.save.inProgress;
    await this.d.store.flush();
    this.card.hide();
    this.disposePlace();
    this.disposeMap();
    this.disposeTown();
    await this.showMap();
    this.endingIn = 0;
  }

  /** 把某城按 unlockOrder 的前 count 个地点标成已通关（走真实的解锁对齐），并刷新当前 DOM 页 */
  async completePlacesForTest(themeId: string, count: number): Promise<void> {
    const th = this.byTheme.get(themeId);
    if (!th) throw new Error(`unknown theme ${themeId}`);
    th.places.slice(0, count).forEach((p) => {
      const s = this.themeSave(themeId).places[p.id];
      s.unlocked = true;
      s.completed = true;
    });
    reconcile(this.save, [th.manifest]);
    await this.d.store.flush();
    this.refreshDomPage();
  }

  /** 注入日期之后：重画当前 DOM 页（首页的今日状态、每日挑战页） */
  refreshDomPage(): void {
    switch (this.page) {
      case 'home':
        return this.showHome();
      case 'cities':
        return this.showCities();
      case 'daily':
        return this.showDaily();
      case 'album':
        return this.showAlbum();
      case 'settings':
        return this.showSettings();
      default:
        return;
    }
  }

  toText() {
    const save = this.save;
    const ts = this.themeSave();
    const unlocked = this.places.filter((p) => ts.places[p.id]?.unlocked).map((p) => p.id);
    const completed = this.places.filter((p) => ts.places[p.id]?.completed).map((p) => p.id);
    const locks = cityLocks(save, this.gates);
    const t = today();
    const dp = this.todayPuzzle();
    const ip = save.inProgress;
    const base = {
      page: this.page,
      themeId: this.themeId,
      endingStory: this.manifest.theme.endingStory,
      themes: this.d.themes.map((th, i) => ({ id: th.id, title: th.title, open: locks[i].open, need: locks[i].need, completed: completedCount(save, th.id) })),
      map: { order: this.places.map((p) => p.id), titles: Object.fromEntries(this.places.map((p) => [p.id, p.title])), unlocked, completed, dayPhase: this.dayNight.phase, is3D: !!this.town && this.page !== 'ending', animating: this.map?.animating ?? false },
      daily: { date: t, themeId: dp.themeId, placeId: dp.place.id, seed: dp.seed, n: dp.n, grid: dp.grid, mods: dp.mods, result: save.daily.results[t] ?? null, streak: currentStreak(save, t), savedStreak: save.daily.streak, bestStreak: save.daily.bestStreak, lastDate: save.daily.lastDate },
      save: {
        onboardingStep: save.onboarding.step,
        intros: save.intros ?? {},
        totalStars: totalStars(save),
        endingSeen: ts.endingSeen,
        gyro: save.gyro,
        settings: save.settings,
        stats: save.stats,
        lastThemeId: save.lastThemeId,
        inProgress: ip ? { mode: ip.mode, themeId: ip.themeId, placeId: ip.placeId, n: ip.n, rows: ip.rows ?? ip.n, moves: ip.moves, elapsedMs: ip.elapsedMs, date: ip.date ?? null } : null,
        places: Object.fromEntries(
          this.places.map((p) => {
            const s = ts.places[p.id];
            return [p.id, { unlocked: s.unlocked, completed: s.completed, catFound: s.catFound, n: this.sizeOf(this.themeId, p.id), grid: this.gridOf(this.themeId, p.id), mods: this.modsOf(this.themeId, p.id), stars: s.stars ?? 0, best: s.best }];
          }),
        ),
      },
      onboarding: this.d.onboarding.stage,
      intro: this.d.onboarding.introKind,
      gyro: { active: this.d.gyro.isActive, enabled: this.d.gyro.enabled, mode: this.d.gyro.mode, permission: this.d.gyro.permission, tilt: { x: +this.d.gyro.tilt.x.toFixed(2), y: +this.d.gyro.tilt.y.toFixed(2) } },
    };
    const card = this.card.model
      ? { placeId: this.card.model.placeId, n: this.card.model.n, grid: this.card.model.grid, mods: this.card.model.mods, stars: this.card.model.stars, best: this.card.model.best, resume: this.card.model.resume, completed: this.card.model.completed, status: this.card.model.status }
      : null;
    const out: Record<string, unknown> = { ...base, card };
    if (this.play && (this.page === 'play' || this.page === 'popup')) {
      Object.assign(out, this.play.session.toText(), {
        page: this.page,
        mode: this.play.mode,
        playThemeId: this.play.themeId,
        placeId: this.play.place.id,
        placeTitle: this.play.place.title,
        locked: this.play.session.locked,
        dailyResult: this.play.daily,
      });
    }
    const popup = this.page === 'popup' ? this.play?.popup : this.page === 'viewPopup' ? this.view?.popup : this.page === 'ending' ? this.town : null;
    if (popup) {
      if (this.view && this.page === 'viewPopup') Object.assign(out, { placeId: this.view.place.id, viewThemeId: this.view.themeId, viewFrom: this.view.from });
      out.popup = {
        phase: popup.phase,
        catFound: popup.catFound,
        runeFound: popup.runeFound,
        tilt: { x: +this.d.gyro.tilt.x.toFixed(2), y: +this.d.gyro.tilt.y.toFixed(2) },
        tiltMode: this.d.gyro.mode,
        gyroPermission: this.d.gyro.permission,
        handedOver: !popup.wantsBoard,
        resultShown: this.resultShown,
      };
    }
    if (this.page === 'album') out.albumTab = this.albumTab;
    const pz = puzzlesOf(this.themeId);
    if (pz) {
      out.dials = pz.dials.map((d) => {
        const ds = this.themeSave().dials?.[String(d.chapter)];
        return { chapter: d.chapter, ready: this.chapterDone(d.chapter), solved: !!ds?.solved, wrong: ds?.wrong ?? 0, hints: ds?.hints ?? 0, slots: ds?.slots ?? null };
      });
      out.runes = Object.fromEntries(this.places.filter((p) => p.rune).map((p) => [p.id, !!ts.places[p.id]?.runeFound]));
      if (this.page === 'dial') out.dialChapter = this.dialChapter;
    }
    if (this.page === 'ending' || this.endingStoryShown) out.ending = { storyShown: this.d.mapUi.endingShown };
    return out;
  }
}
